import DownloadManager from "@/types/DownloadManager/DownloadManager";
import { SceneManager } from "@/types/LCAoM/SceneManager";
import { LmViewer3D } from "@/types/LmViewer3D/LmViewer3D";
import { ModelTreeNode } from "@/types/ModelTreeNode";
import { ProgressIndicator } from "@/types/ProgressIndicator";
import { SelectionManager } from "@/types/SelectionManager";
import { ViewerToken } from "@/types/ViewerToken";
import { ErrorManager } from "@/types/errors/ErrorManager";
import { SankeyOptionKeys, SankeyOptions } from "@/types/sankeyOptions";
import { ApplicationSettings, CadModelCategory, FeatureManagementType, Model, NodeType, UserProfile } from "@api/apiClient";
import { Theme } from "@mui/material";
import JobStatusStore from "@stores/JobStatusStore";
import { QueryCache, QueryClient } from "@tanstack/react-query";
import _ from "lodash";
import { makeAutoObservable, toJS } from "mobx";
import { EtlJobStatusStore } from "./EtlJobStatusStore";
// import { jwtDecode } from 'jwt-decode';

// Add to the global window object for auto-completion in the console
// > window.AppStore
export { };
declare global {
  interface Window {
    AppStore: AppStore;
  }
}

/** Scope item interface matching ModelScopeSettingsItemRespone from API */
export interface ScopeItem {
  id?: number;
  name?: string;
  modelScopeSettingsItemId?: string;
  [key: string]: any;
}

/** 
 * Maps NodeType numeric values (from C# enum) to string labels.
 * The numeric order must match the C# NodeType enum definition.
 */
const nodeTypeLabels: Record<number, string> = Object.fromEntries(
  Object.values(NodeType).map((value, index) => [index, value])
);

class AppStore {
  public persistSettings = ["isModelEnabled", "colorMode", "language"];
  public isAuthenticated: boolean = false;
  public isInitialized: boolean = false;
  public isInitializing: boolean = false;
  public isInitializingCount: number = 0;
  public isModelEnabled: boolean = true;
  public isProjectStepperEnabled: boolean = true;
  public isLoggingEnabled: boolean = false;
  public isProfilingEnabled: boolean = false;
  public showDevtools: boolean = false;
  public isLmViewerEnabled: boolean = true;
  public isInventoryEnabled: boolean = true;
  /** 
   * Grid data source - undefined until setDefaults is called.
   * This prevents hooks from running before app initialization.
   */
  public language: string = "en";
  public isPending: boolean = false;
  public isPendingCount: number = 0;
  public userProfile: UserProfile | null = null;
  public applicationSettings: ApplicationSettings | null = null;
  public isMemoryPackEnabled: boolean = true;
  /**
   * Material assignment save mode.
   * true (default): Auto-save material assignments to server after calculation
   * false: Accumulate changes locally for manual save via Save button
   */
  public autoSaveMaterialAssignments: boolean = true;
  /**
   * Tree grouping hierarchy preservation mode.
   * true (default): Only Element nodes are grouped. Component nodes remain as children 
   * of their parent Elements, and Supplemental nodes remain as children of their 
   * parent Elements/Components.
   * false: All node types are treated equally during grouping. Use this when 
   * applying custom GroupingFields that should apply to all nodes.
   */
  public preserveGroupingHierarchy: boolean = true;
  /**
   * Delay in milliseconds for grid loading tasks (downloads and tree building).
   * Set to 0 for no delay (normal operation).
   * Set to a positive value (e.g., 3000) to add artificial delays between
   * loading phases for testing the LoadingOverlay progress UI.
   */
  public gridLoadingTasksDelay: number = 0;
  /**
   * When true (default), enables string interning during tree building and scene loading
   * to reduce memory usage. Shared by Assign tab and scene/analysis loading.
   */
  public enableStringInterning: boolean = true;
  /*
   * Settings Drawer State
   */
  public openSettings: boolean = false;
  /*
   * Theme State
   */
  public colorMode: "light" | "dark" = "light";
  public defaultTheme!: Theme;
  /*
   * Global Error Manager
   */
  public errorManager: ErrorManager = new ErrorManager();
  /*
   * React Query global state
   */
  public queryClient: QueryClient;
  public queryCache: QueryCache;
  /*
   * Header - Progress Indicator
   */
  public progressIndicator: ProgressIndicator = new ProgressIndicator();
  /*
   * Results tab setting state
   */
  public openResultsSettings: boolean = false;
  public showGrid: boolean = true;
  public showChart: boolean = true;
  public showSankey: boolean = false;
  public sankeyOptions: SankeyOptions = {
    showScenarios: true,
    showModels: true,
    showCategories: true,
    showCadMaterials: true,
    showCo2MaterialTypes: true,
    showSystems: true,
    showSubSystems: true,
    showAttributes: true,
    showEmptyNodes: true,
    showLayerTitles: true,
  };

  /*
   * Currently Selected Project, Scenario and Model
   */
  /*
   * Assign Grid state (legacy - useScenarioModel)
   */
  public assignNodes: ModelTreeNode[] | null = [];
  public assignNodesRecalc: number = 0;
  public expandedKeys: any = {};
  public selectedNodesKeys: any = {};
  public selectedModelCategories: Record<string, ModelTreeNode> = {};
  /*
   * Node Grid state (new - useNodes)
   * SceneManager contains Scene, tree rows, and helper methods
   */
  public sceneManager: SceneManager = new SceneManager();
  /*
   * Assign Grid temporary state for non-modelled edits
   */
  public nonModelledPercentEdits: Record<string, any> = {};
  public nonModelledVolumeEdits: Record<string, any> = {};
  public nonModelledCO2eEdits: Record<string, any> = {};
  /*
   * MaterialSelectDialog selected material libraries
   */
  public co2MaterialLibraryIds: string[] = [];
  /*
   * 3D Viewers and Selection State
   */
  public selectionManager: SelectionManager = new SelectionManager();
  public lmViewer1: LmViewer3D | undefined;
  public lmViewer2: LmViewer3D | undefined;
  public downloadManager: DownloadManager;
  /*
   * Design Automation and Model Derivative Jobs Status
   */
  public jobStatus: JobStatusStore = new JobStatusStore();
  public etlJobStatus: EtlJobStatusStore = new EtlJobStatusStore();
  /*
   * Backup of console command for enable/disable logging
   */
  public consoleLog: any;
  /*
   * Viewer Token
   */
  public viewerToken: ViewerToken = new ViewerToken();
  /*
   * Project Tab Bar Splitter Sizes
   */
  public projectTabBarSizes: number[] = [50.0, 50.0];
  /*
   * Material Select Dialog Splitter Sizes
   */
  public materialSelectDialogSizes: number[] = [50.0, 50.0];
  /*
   * Scenario Grid refresh clone
   */
  public scenarioName: string = '';
  /*
   * Model refresh copy material
   */
  public modelName: string = '';

  /*
   * Lookup Maps for resolving IDs to display labels
   * These are used by TreeBuilder and grid columns
   */
  public cadModelCategoryLookup: Map<string, CadModelCategory> = new Map();
  public scopeLookup: Map<number, ScopeItem> = new Map();
  public modelLookup: Map<string, Model> = new Map();

  constructor(queryClient: QueryClient, queryCache: QueryCache) {
    window.AppStore = this;
    makeAutoObservable(
      this,
      {
        defaultTheme: false,

        assignNodes: false,
        expandedKeys: false,
        selectedNodesKeys: true,
        selectedModelCategories: false,

        sceneManager: false,

        // Lookup Maps are observable but not deep
        cadModelCategoryLookup: true,
        scopeLookup: true,
        modelLookup: true,

        viewer1: false,
        viewer2: false,
        lmViewer1: false,
        lmViewer2: false,
      },
      {
        autoBind: true,
      },
    );

    this.queryClient = queryClient;
    this.queryCache = queryCache;
    this.downloadManager = new DownloadManager(this, { workerThreadLimit: 2 });

    // TODO: change this to use mitt events
    this.selectionManager.onLoad = this.onLoad.bind(this);
    this.selectionManager.onUnload = this.onUnload.bind(this);

    //this.selectionManager.emitter.on(SelectionManagerEvents.SELECTION_ONLOAD_EVENT, this.onLoad.bind(this));
    //this.selectionManager.emitter.on(SelectionManagerEvents.SELECTION_ONUNLOAD_EVENT, this.onUnload.bind(this));
  }

  get dump() {
    return toJS(this);
  }

  setEnableStringInterning = (value: boolean): void => {
    this.enableStringInterning = value;
  };

  /**
   * Get the APS viewer for the LMV Viewer1 3D viewer.
   * Use .lmViewer1 to access the LmViewer3D wrapper for viewer1.
   * 
   * @returns {Autodesk.Viewing.GuiViewer3D | undefined}
   */
  get viewer1(): Autodesk.Viewing.GuiViewer3D | undefined {
    return this.lmViewer1?.viewer;
  }

  /**
   * Get the APS viewer for the LMV Viewer2 3D viewer.
   * Use .lmViewer2 to access the LmViewer3D wrapper for viewer2.
   * 
   * @returns {Autodesk.Viewing.GuiViewer3D | undefined}
   */
  get viewer2(): Autodesk.Viewing.GuiViewer3D | undefined {
    return this.lmViewer2?.viewer;
  }

  get settings() {
    return this.applicationSettings?.settings || {};
  }

  get features() {
    return this.applicationSettings?.features || [];
  }

  get metadataEntities() {
    return this.applicationSettings?.metadataEntities || [];
  }

  hasFeature = (feature: FeatureManagementType): boolean => {
    return this.features.includes(feature);
  };

  // ============================================================================
  // Lookup Initialization and Getter Methods
  // ============================================================================

  /**
   * Initializes the CadModelCategory lookup from API data.
   */
  initCadModelCategoryLookup = (categories: CadModelCategory[] | undefined): void => {
    this.cadModelCategoryLookup = new Map();
    if (!categories) return;
    for (const category of categories) {
      if (category.cadModelCategoryId) {
        this.cadModelCategoryLookup.set(category.cadModelCategoryId, category);
      }
    }
  };

  /**
   * Gets the label for a CadModelCategory by its ID.
   */
  getCadModelCategoryLabel = (categoryId: string | undefined): string => {
    if (!categoryId) return '';
    const category = this.cadModelCategoryLookup.get(categoryId);
    return category?.label ?? '';
  };

  /**
   * Initializes the Scope lookup from app settings data.
   */
  initScopeLookup = (scopeItems: ScopeItem[] | undefined): void => {
    this.scopeLookup = new Map();
    if (!scopeItems) return;
    for (const item of scopeItems) {
      if (item.id) {
        this.scopeLookup.set(item.id, item);
      }
    }
  };

  /**
   * Gets the name for a Scope by its ID.
   */
  getScopeName = (scopeId: number | null | undefined): string => {
    if (scopeId === undefined || scopeId === null) return '';
    const scope = this.scopeLookup.get(scopeId);
    return scope?.name ?? '';
  };

  /**
   * Gets the raw scope lookup as a Record (for backwards compatibility).
   * Keys are converted to strings for compatibility with grid valueOptions.
   */
  getScopeLookupMap = (): Record<string, ScopeItem> => {
    const map: Record<string, ScopeItem> = {};
    for (const [id, scope] of this.scopeLookup) {
      map[String(id)] = scope;
    }
    return map;
  };

  /**
   * Initializes the Model lookup from project models.
   */
  initModelLookup = (models: Model[] | undefined): void => {
    this.modelLookup = new Map();
    if (!models) return;
    for (const model of models) {
      if (model.modelId) {
        this.modelLookup.set(model.modelId, model);
      }
    }
  };

  /**
   * Gets the display name for a Model by its ID.
   * Uses apsItemName if available, falls back to modelName, then modelId.
   */
  getModelDisplayName = (modelId: string | undefined): string => {
    if (!modelId) return '';
    const model = this.modelLookup.get(modelId);
    if (!model) return modelId;
    return model.apsItemName ?? model.modelName ?? modelId;
  };

  /**
   * Gets the display label for a NodeType numeric value.
   */
  getNodeTypeLabel = (nodeType: number | undefined): string => {
    if (nodeType === undefined || nodeType === null) return '';
    return nodeTypeLabels[nodeType] ?? String(nodeType);
  };

  /**
   * Resolves a field value to a display label for TreeBuilder grouping.
   * Uses the appropriate lookup based on the field name.
   */
  resolveGroupLabel = (field: string, value: string | number | undefined): string => {
    if (value === undefined || value === null) return '';

    switch (field) {
      case 'cadModelCategoryId':
        return this.getCadModelCategoryLabel(String(value)) || String(value);

      case 'scopeId':
        const numericScopeId = typeof value === 'number' ? value : null;
        return this.getScopeName(numericScopeId) || String(value);

      case 'modelId':
        return this.getModelDisplayName(String(value)) || String(value);

      case 'nodeType':
        return this.getNodeTypeLabel(value as number);

      case 'systemId':
      case 'system':
        return this.getSystemNameById(String(value)) || String(value);

      case 'subSystemId':
      case 'subSystem':
        return this.getSubSystemNameById(String(value)) || String(value);

      default:
        return String(value);
    }
  };

  /**
   * Gets the system name by ID from metadataEntities.
   */
  getSystemNameById = (systemId: string): string => {
    if (!systemId) return '';
    const system = this.metadataEntities.find(
      (e: any) => e.type === 'System' && e.metadataEntityId === systemId
    );
    return system?.name ?? '';
  };

  /**
   * Gets the subsystem name by ID from metadataEntities.
   */
  getSubSystemNameById = (subSystemId: string): string => {
    if (!subSystemId) return '';
    // Search in children of systems
    for (const system of this.metadataEntities) {
      if (system.type === 'System' && system.children) {
        const subSystem = system.children.find(
          (c: any) => c.type === 'Subsystem' && c.metadataEntityId === subSystemId
        );
        if (subSystem) return subSystem.name ?? '';
      }
    }
    return '';
  };

  // TODO: change these to use mitt events
  onLoad = async (scenarioId: string, viewerId: string, urns: string | string[]) => {
    console.log(`AppStore:onLoad(${scenarioId}, ${viewerId}, ${urns})`);

    const urnArray = Array.isArray(urns) ? urns : [urns];

    if (this.isModelEnabled && this.isLmViewerEnabled) {
      try {
        if (viewerId === "viewer1") {
          for (const urn of urnArray) {
            await this.lmViewer1?.loadDocument(urn);
          }
        }
        if (viewerId === "viewer2") {
          for (const urn of urnArray) {
            await this.lmViewer2?.loadDocument(urn);
          }
        }
      } catch (error) {
        console.log("error: ", error);
      }
    }
    // Start downloads in the background - convert URNs to modelIds
    const modelIds = this.selectionManager.urnsToModelIds(urnArray);

    if (modelIds.length > 0) {
      this.downloadManager.enqueueLoad(modelIds);
    }
  };

  // TODO: change these to use mitt events
  onUnload = async (scenarioId: string, viewerId: string, urns: string | string[]) => {
    console.log(`AppStore:onUnload(${scenarioId}, ${viewerId}, ${urns})`);

    const urnArray = Array.isArray(urns) ? urns : [urns];

    if (this.isModelEnabled && this.isLmViewerEnabled) {
      try {
        if (viewerId === "viewer1") {
          for (const urn of urnArray) {
            await this.lmViewer1?.unloadDocument(urn);
          }
        }
        if (viewerId === "viewer2") {
          for (const urn of urnArray) {
            await this.lmViewer2?.unloadDocument(urn);
          }
        }
      } catch (error) {
        console.log("error: ", error);
      }
    }

    // Cancel downloads if any
    const modelIds =
      this.selectionManager.selectedProject?.models
        ?.filter((m: Model) => urnArray.includes(m.apsViewId!))
        .map((m: Model) => m.modelId!) || [];
    if (modelIds.length > 0) {
      this.downloadManager.cancel(modelIds);
    }
  };

  setDefaults = () => {
    this.setColorMode(localStorage.getItem("colorMode") === "dark" ? "dark" : "light");
    this.setIsModelEnabled(localStorage.getItem("isModelEnabled") !== "false");
    this.setIsProjectStepperEnabled(localStorage.getItem("isProjectStepperEnabled") !== "false");
    this.setIsLoggingEnabled(localStorage.getItem("isLoggingEnabled") === "true");
    this.setIsProfilingEnabled(localStorage.getItem("isProfilingEnabled") === "true");
    this.setShowDevtools(
      localStorage.getItem("showDevtools") !== null
        ? localStorage.getItem("showDevtools") === "true"
        : import.meta.env.DEV,
    );
    this.setIsLmViewerEnabled(localStorage.getItem("isLmViewerEnabled") !== "false");
    this.setIsInventoryEnabled(localStorage.getItem("isInventoryEnabled") !== "false");
    this.setMaterialLibraryIds(localStorage.getItem("co2MaterialLibraryIds")?.split(",") || []);
    const sizes1 = JSON.parse(localStorage.getItem("projectTabBarSizes") || "[50.0, 50.0]");
    this.setProjectTabBarSizes(sizes1);
    const sizes2 = JSON.parse(localStorage.getItem("materialSelectDialogSizes") || "[50.0, 50.0]");
    this.setMaterialSelectDialogSizes(sizes2);

    this.setShowGrid(localStorage.getItem("showResultsGrid") !== "false");
    this.setShowChart(localStorage.getItem("showResultsChart") !== "false");
    this.setShowSankey(localStorage.getItem("showResultsSankey") === "true");
    const sankeyOptions = JSON.parse(localStorage.getItem("sankeyOptions") || "{}");
    this.setSankeyOptions(sankeyOptions);
  };

  setUserProfile = (userProfile: UserProfile | null) => {
    this.userProfile = userProfile;
  };

  setOpenSettings = (openSettings: boolean) => {
    this.openSettings = openSettings;
  };

  toggleOpenSettings = () => {
    this.openSettings = !this.openSettings;
  };

  setIsModelEnabled = (isModelEnabled: boolean) => {
    this.isModelEnabled = isModelEnabled;
    localStorage.setItem("isModelEnabled", this.isModelEnabled.toString());
  };

  toggleIsModelEnabled = () => {
    this.isModelEnabled = !this.isModelEnabled;
    localStorage.setItem("isModelEnabled", this.isModelEnabled.toString());
  };

  setAuthenticated = (isAuthenticated: boolean) => {
    this.isAuthenticated = isAuthenticated;
  };

  setInitialized = (isInitialized: boolean) => {
    this.isInitialized = isInitialized;
  };

  setIsInitializing = (isLoading: boolean) => {
    this.isInitializingCount = Math.max(0, this.isInitializingCount + (isLoading ? 1 : -1));
    this.isInitializing = this.isInitializingCount > 0;
  };

  setLanguage = (language: string) => {
    //console.log("setLanguage: ", language);
    this.language = language;
  };

  // setProject removed - use selectionManager.setProject

  // setProjectPermissions = (permissions: string[]) => {
  //   this.projectPermissions = permissions;
  // };

  setAppSettings = (appSettings: ApplicationSettings) => {
    this.applicationSettings = appSettings;
  };

  // setScenario removed - use selectionManager.setScenario

  setMaterialLibraryIds = (materialLibraryIds: string[]) => {
    this.co2MaterialLibraryIds = materialLibraryIds;
    localStorage.setItem("co2MaterialLibraryIds", this.co2MaterialLibraryIds.join(","));
  };

  setAssignNodes = (assignNodes: ModelTreeNode[] | null) => {
    this.assignNodes = assignNodes;
    this.assignNodesRecalc++;
    //console.log("appStore.assignNodes: ", toJS(this.assignNodes));
  };

  // setModel removed - use selectionManager.setModel

  // setDefaultModel removed - use selectionManager.setDefaultModel

  setSelectedModelCategories = (selectedModelCategories: Record<string, ModelTreeNode>) => {
    this.selectedModelCategories = selectedModelCategories || {};
  };

  setExpandedKeys = (expandedKeys: any) => {
    //console.log("setExpandedKeys: ", expandedKeys);
    this.expandedKeys = expandedKeys || {};
  };

  setSelectedNodesKeys = (selectedNodesKeys: any) => {
    this.selectedNodesKeys = selectedNodesKeys || {};
  };

  setIsLoading = (isLoading: boolean) => {
    this.isPendingCount = Math.max(0, this.isPendingCount + (isLoading ? 1 : -1));
    this.isPending = this.isPendingCount > 0;
  };

  setColorMode = (colorMode: string) => {
    this.colorMode = <any>colorMode;
    localStorage.setItem("colorMode", this.colorMode);
  };

  setDefaultTheme = (theme: Theme) => {
    this.defaultTheme = theme;
  };

  toggleColorMode = () => {
    this.colorMode = this.colorMode === "light" ? "dark" : "light";
    localStorage.setItem("colorMode", this.colorMode);
  };

  setIsProjectStepperEnabled = (isProjectStepperEnabled: boolean) => {
    this.isProjectStepperEnabled = isProjectStepperEnabled;
    localStorage.setItem("isProjectStepperEnabled", this.isProjectStepperEnabled.toString());
  };

  toggleIsProjectStepperEnabled = () => {
    this.isProjectStepperEnabled = !this.isProjectStepperEnabled;
    localStorage.setItem("isProjectStepperEnabled", this.isProjectStepperEnabled.toString());
  };

  setIsLoggingEnabled = (isLoggingEnabled: boolean) => {
    this.isLoggingEnabled = isLoggingEnabled;
    localStorage.setItem("isLoggingEnabled", this.isLoggingEnabled.toString());

    if (!isLoggingEnabled) {
      if (!this.consoleLog) {
        this.consoleLog = console.log;
      }
      console.log = function () { };
    } else {
      if (this.consoleLog) {
        console.log = this.consoleLog;
      }
    }
  };

  setIsProfilingEnabled = (isProfilingEnabled: boolean) => {
    this.isProfilingEnabled = isProfilingEnabled;
    localStorage.setItem("isProfilingEnabled", this.isProfilingEnabled.toString());

    if (!isProfilingEnabled) {
    } else {
    }
  };

  toggleIsLoggingEnabled = () => {
    this.isLoggingEnabled = !this.isLoggingEnabled;
    localStorage.setItem("isLoggingEnabled", this.isLoggingEnabled.toString());
  };

  toggleIsProfilingEnabled = () => {
    this.isProfilingEnabled = !this.isProfilingEnabled;
    localStorage.setItem("isProfilingEnabled", this.isProfilingEnabled.toString());
  };

  setShowDevtools = (showDevtools: boolean) => {
    this.showDevtools = showDevtools;
    localStorage.setItem("showDevtools", this.showDevtools.toString());
  };

  toggleShowDevtools = () => {
    this.showDevtools = !this.showDevtools;
    localStorage.setItem("showDevtools", this.showDevtools.toString());
  };

  setIsLmViewerEnabled = (isLmViewerEnabled: boolean) => {
    this.isLmViewerEnabled = isLmViewerEnabled;
    localStorage.setItem("isLmViewerEnabled", this.isLmViewerEnabled.toString());
  };

  toggleIsLmViewerEnabled = () => {
    this.isLmViewerEnabled = !this.isLmViewerEnabled;
    localStorage.setItem("isLmViewerEnabled", this.isLmViewerEnabled.toString());
  };

  setIsInventoryEnabled = (isInventoryEnabled: boolean) => {
    this.isInventoryEnabled = isInventoryEnabled;
    localStorage.setItem("isInventoryEnabled", this.isInventoryEnabled.toString());
  };

  toggleOpenResultsSettings = () => {
    this.openResultsSettings = !this.openResultsSettings;
  };

  setShowGrid = (showGrid: boolean) => {
    this.showGrid = showGrid;
    localStorage.setItem("showResultsGrid", this.showGrid.toString());
  };

  toggleShowGrid = () => {
    this.showGrid = !this.showGrid;
    localStorage.setItem("showResultsGrid", this.showGrid.toString());
  };

  setShowChart = (showChart: boolean) => {
    this.showChart = showChart;
    localStorage.setItem("showResultsChart", this.showChart.toString());
  };

  toggleShowChart = () => {
    this.showChart = !this.showChart;
    localStorage.setItem("showResultsChart", this.showChart.toString());
  };

  setShowSankey = (showSankey: boolean) => {
    this.showSankey = showSankey;
    localStorage.setItem("showResultsSankey", this.showSankey.toString());
  };

  toggleShowSankey = () => {
    this.showSankey = !this.showSankey;
    localStorage.setItem("showResultsSankey", this.showSankey.toString());
  };

  setSankeyOptions = (sankeyOptions: SankeyOptions) => {
    this.sankeyOptions = _.merge(this.sankeyOptions, sankeyOptions);
    localStorage.setItem("sankeyOptions", JSON.stringify(this.sankeyOptions));
  };

  toggleSankeyOption = (option: SankeyOptionKeys) => {
    this.sankeyOptions[option] = !this.sankeyOptions[option];
    this.sankeyOptions = _.merge({}, this.sankeyOptions);
    localStorage.setItem("sankeyOptions", JSON.stringify(this.sankeyOptions));
  };

  setLMViewer1 = (lmViewer: LmViewer3D | null) => {
    if (lmViewer === null) {
      this.lmViewer1?.destroy();
    }
    this.lmViewer1 = lmViewer || undefined;
  };

  setLMViewer2 = (lmViewer: LmViewer3D | null) => {
    if (lmViewer === null) {
      this.lmViewer2?.destroy();
    }
    this.lmViewer2 = lmViewer || undefined;
  };

  findLmViewer = (urn: string): LmViewer3D | undefined => {
    if (this.lmViewer1?.urns.includes(urn)) {
      return this.lmViewer1;
    }
    if (this.lmViewer2?.urns.includes(urn)) {
      return this.lmViewer2;
    }
  };

  getAdskThemeClass = () => {
    return this.colorMode === "dark" ? "dark-theme" : "light-theme";
  };

  getSelectedModels = (scenarioId: string): Model[] => {
    const models = this.selectionManager.selectedProject?.models?.filter((m: Model) => this.selectionManager.getSelectedModelUrns(scenarioId).includes(m.apsViewId!)) || [];
    return models;
  };

  setProjectTabBarSizes = (projectTabBarSizes: number[]) => {
    this.projectTabBarSizes = projectTabBarSizes;
    localStorage.setItem("projectTabBarSizes", JSON.stringify(this.projectTabBarSizes));
  };

  setMaterialSelectDialogSizes = (materialSelectDialogSizes: number[]) => {
    this.materialSelectDialogSizes = materialSelectDialogSizes;
    localStorage.setItem("materialSelectDialogSizes", JSON.stringify(this.materialSelectDialogSizes));
  };

  // TODO: this is capturing the scenarioName not scenarioId
  setCloneScenarioId = (scenarioName: string) => {
    this.scenarioName = scenarioName;
  };

  setCopyModelMaterial = (modelName: string) => {
    this.modelName = modelName;
  };
}

export default AppStore;
